import React from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { 
  Container, 
  AppBar, 
  Toolbar, 
  Typography, 
  Box,
  Paper,
  IconButton,
  Button
} from '@mui/material';
import { Home, ArrowBack, ArrowForward } from '@mui/icons-material';
import { Link } from 'react-router-dom';

const theme = createTheme({
  palette: {
    primary: { main: '#1976d2' },
    secondary: { main: '#dc004e' },
  },
});

function MUIBasicsDocsPage() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      
      <AppBar position="static">
        <Toolbar>
          <IconButton 
            component={Link} 
            to="/" 
            color="inherit" 
            sx={{ mr: 2 }}
          >
            <Home />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            MUI Basics Documentation
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        
        <Paper sx={{ p: 4 }}>
          <Typography variant="h3" gutterBottom>
            MUI 기본 학습 정리
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 3 }}>
            Material-UI는 Google의 Material Design을 React로 구현한 컴포넌트 라이브러리입니다. 
            일관된 디자인과 접근성을 제공하며, 빠른 개발이 가능합니다.
          </Typography>

          <Typography variant="h4" sx={{ mt: 4, mb: 2 }}>
            설치
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            MUI를 사용하기 위해 필요한 핵심 패키지들을 설치합니다.
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography component="pre" sx={{ fontFamily: 'monospace' }}>
{`npm install @mui/material @emotion/react @emotion/styled @mui/icons-material`}
            </Typography>
          </Paper>

          <Typography variant="h4" sx={{ mt: 4, mb: 2 }}>
            기본 설정
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            ThemeProvider로 전체 앱의 테마를 설정하고, CssBaseline으로 브라우저별 기본 스타일을 초기화합니다.
          </Typography>
          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            ThemeProvider 설정
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
{`import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

const theme = createTheme({
  palette: {
    primary: { main: '#1976d2' },
    secondary: { main: '#dc004e' },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* 나머지 컴포넌트들 */}
    </ThemeProvider>
  );
}`}
            </Typography>
          </Paper>

          <Typography variant="h4" sx={{ mt: 4, mb: 2 }}>
            주요 컴포넌트
          </Typography>
          <Typography variant="body2" sx={{ mb: 3 }}>
            MUI의 핵심 컴포넌트들을 익혀보겠습니다. 이 컴포넌트들은 대부분의 웹 애플리케이션에서 자주 사용됩니다.
          </Typography>
          
          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            1. Layout 컴포넌트
          </Typography>
          
          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
            Container
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
{`<Container maxWidth="md">
  {/* 내용 */}
</Container>`}
            </Typography>
          </Paper>
          <Typography variant="body2" sx={{ mt: 1 }}>
            • maxWidth: xs, sm, md, lg, xl
          </Typography>

          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
            Box
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
{`<Box sx={{ p: 2, m: 1 }}>
  {/* 내용 */}
</Box>`}
            </Typography>
          </Paper>
          <Typography variant="body2" sx={{ mt: 1 }}>
            • 범용 레이아웃 컨테이너<br/>
            • sx prop으로 스타일 적용
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            2. 버튼
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
{`<Button variant="contained">기본 버튼</Button>
<Button variant="outlined">테두리 버튼</Button>
<Button variant="text">텍스트 버튼</Button>

<Button startIcon={<AddIcon />}>아이콘 버튼</Button>
<Button size="small">작은 버튼</Button>`}
            </Typography>
          </Paper>
          <Typography variant="body2" sx={{ mt: 1 }}>
            • variant: contained, outlined, text<br/>
            • size: small, medium, large<br/>
            • color: primary, secondary, error, warning, info, success
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            3. 입력 필드
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
{`<TextField label="제품명" variant="outlined" />
<TextField label="수량" type="number" variant="filled" />
<TextField label="설명" multiline rows={4} />`}
            </Typography>
          </Paper>
          <Typography variant="body2" sx={{ mt: 1 }}>
            • variant: outlined, filled, standard
          </Typography>

          <Typography variant="h4" sx={{ mt: 4, mb: 2 }}>
            스타일링 (sx prop)
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            sx prop은 MUI에서 제공하는 강력한 스타일링 방법입니다. CSS-in-JS 방식으로 동적 스타일링과 테마 접근이 가능합니다.
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
{`<Box sx={{
  p: 2,           // padding: 16px
  m: 1,           // margin: 8px
  mb: 3,          // marginBottom: 24px
  width: '100%',  // width: 100%
  bgcolor: 'primary.main',  // 테마 색상
  '& button': {   // 자식 요소 스타일링
    m: 0.5
  }
}}>`}
            </Typography>
          </Paper>
          <Typography variant="body2" sx={{ mt: 1 }}>
            • 간격 단위: 0.5 = 4px, 1 = 8px, 2 = 16px, 3 = 24px, 4 = 32px
          </Typography>

        </Paper>

        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'space-between' }}>
          <Button 
            component={Link}
            to="/"
            variant="outlined"
            startIcon={<ArrowBack />}
          >
            Back to Home
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              component={Link}
              to="/docs/mui-theme"
              variant="contained"
              endIcon={<ArrowForward />}
            >
              Next: Theme
            </Button>
            <Button 
              component={Link}
              to="/docs/mui-layout"
              variant="outlined"
            >
              Layout Guide
            </Button>
          </Box>
        </Box>

      </Container>
    </ThemeProvider>
  );
}

export default MUIBasicsDocsPage;
